import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CannedResponsesClient } from "@/api/cannedResponsesClient";
import { useWorkspace } from "@/contexts/WorkspaceContext";

/**
 * Loads and mutates canned replies created by the signed-in agent in the active workspace.
 */
export function useMyCannedResponses() {
  const { activeWorkspaceId } = useWorkspace();
  const client = useMemo(() => new CannedResponsesClient(), []);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadIdRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!activeWorkspaceId || !String(activeWorkspaceId).trim()) {
      setItems([]);
      setError(null);
      setLoading(false);
      return;
    }
    const loadId = ++loadIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const { data } = await client.listMine({ workspaceId: activeWorkspaceId });
      if (loadId !== loadIdRef.current) return;
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      if (loadId !== loadIdRef.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
      setItems([]);
    } finally {
      if (loadId === loadIdRef.current) setLoading(false);
    }
  }, [activeWorkspaceId, client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const updateCannedResponse = useCallback(
    async (payload) => {
      if (!activeWorkspaceId || !String(activeWorkspaceId).trim()) {
        throw new Error("Select a workspace to edit saved replies.");
      }
      const { data } = await client.update({ ...payload, workspaceId: activeWorkspaceId });
      setItems((prev) => prev.map((row) => (row.id === data.id ? data : row)));
      return data;
    },
    [activeWorkspaceId, client]
  );

  const deleteCannedResponse = useCallback(
    async (id) => {
      if (!activeWorkspaceId || !String(activeWorkspaceId).trim()) {
        throw new Error("Select a workspace to delete saved replies.");
      }
      await client.delete({ id, workspaceId: activeWorkspaceId });
      setItems((prev) => prev.filter((row) => row.id !== id));
    },
    [activeWorkspaceId, client]
  );

  return {
    items,
    loading,
    error,
    refresh,
    updateCannedResponse,
    deleteCannedResponse,
    workspaceId: activeWorkspaceId,
  };
}
